"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePrenotaStore } from "@PN/inclusao";
import type { ParcelaPayload, DateISO } from "@PN/inclusao";
import { useParcelasQuery } from "./_tanstack";
import { useParcelasValidation } from "./useParcelasValidation";
import { fmt } from "visuais";

export function useParcelas() {
  // ✅ CORRETO - Seletores separados para evitar loop infinito
  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const setPagamentos = usePrenotaStore(
    (state) => state.actions.draft.setPagamentos
  );

  // Hook de validação separado
  const {
    valorNF,
    canFetch,
    params,
    parcelasBaseProtheus,
    parcelasValorBaseProtheus,
    parcelasValorBaseProtheusNormalizado,
    parcelasValorBaseProtheusPorIndice,
  } = useParcelasValidation();

  // Estado local: parcelas salvas na store (sempre prioritárias)
  const parcelas = pagamentos.parcelas;
  const condicaoAtualCodigo = (pagamentos.condicaoFinanceira || "")
    .trim()
    .toUpperCase();
  const lastCondicaoCodigoRef = useRef(condicaoAtualCodigo);
  const shouldOverwriteByCondicaoChangeRef = useRef(false);
  const normalizeItemKey = useCallback((item?: string) => {
    const trimmed = (item || "").trim();
    if (!trimmed) return "";
    const onlyDigits = trimmed.replace(/\D/g, "");
    if (!onlyDigits) return trimmed;
    const normalized = String(Number(onlyDigits));
    return normalized === "NaN" ? trimmed : normalized;
  }, []);
  const parcelasManuaisRef = useRef<Set<string>>(new Set());
  const parcelasBaseCount = useMemo(() => {
    const byDate = Object.keys(parcelasBaseProtheus || {}).length;
    const byValue = Object.keys(parcelasValorBaseProtheus || {}).length;
    const byIndex = parcelasValorBaseProtheusPorIndice?.length || 0;
    return Math.max(byDate, byValue, byIndex);
  }, [
    parcelasBaseProtheus,
    parcelasValorBaseProtheus,
    parcelasValorBaseProtheusPorIndice,
  ]);
  const isParcelaBaseBySnapshot = useCallback(
    (parcela: ParcelaPayload, idx: number) => {
      const hasAnyBaseInfo =
        Object.keys(parcelasBaseProtheus || {}).length > 0 ||
        Object.keys(parcelasValorBaseProtheus || {}).length > 0 ||
        (parcelasValorBaseProtheusPorIndice?.length || 0) > 0;
      if (!hasAnyBaseInfo) {
        // Sem base capturada, prioriza proteção para não liberar remoção indevida.
        return true;
      }
      if (typeof parcelasBaseProtheus?.[parcela.item] === "string") return true;
      if (
        typeof parcelasValorBaseProtheus?.[parcela.item] === "number" &&
        Number.isFinite(parcelasValorBaseProtheus?.[parcela.item])
      ) {
        return true;
      }
      const normalized = normalizeItemKey(parcela.item);
      if (
        normalized &&
        typeof parcelasValorBaseProtheusNormalizado?.[normalized] === "number" &&
        Number.isFinite(parcelasValorBaseProtheusNormalizado?.[normalized])
      ) {
        return true;
      }
      return idx < parcelasBaseCount;
    },
    [
      parcelasBaseProtheus,
      parcelasValorBaseProtheus,
      parcelasValorBaseProtheusNormalizado,
      parcelasBaseCount,
      normalizeItemKey,
    ]
  );

  useEffect(() => {
    // Reidrata de forma simples: tudo que não estiver na base do Protheus vira manual.
    const nextManual = new Set<string>();
    parcelas.forEach((parcela, idx) => {
      if (parcelasManuaisRef.current.has(parcela.item)) {
        nextManual.add(parcela.item);
        return;
      }
      if (!isParcelaBaseBySnapshot(parcela, idx)) {
        nextManual.add(parcela.item);
      }
    });
    parcelasManuaisRef.current = nextManual;
  }, [parcelas, isParcelaBaseBySnapshot]);

  const isParcelaBaseProtheus = useCallback(
    (idx: number) => {
      const parcela = parcelas[idx];
      if (!parcela) return false;
      if (parcelasManuaisRef.current.has(parcela.item)) return false;
      return isParcelaBaseBySnapshot(parcela, idx);
    },
    [parcelas, isParcelaBaseBySnapshot]
  );

  useEffect(() => {
    if (lastCondicaoCodigoRef.current !== condicaoAtualCodigo) {
      shouldOverwriteByCondicaoChangeRef.current = true;
      lastCondicaoCodigoRef.current = condicaoAtualCodigo;
    }
  }, [condicaoAtualCodigo]);

  // Consulta as parcelas da API - habilitada quando pode buscar
  const {
    data: parcelasApi,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useParcelasQuery(params, canFetch);

  // Verifica se as parcelas estão salvas e consistentes
  const areParcelasSaved = useMemo(() => {
    if (parcelas.length === 0) return false;
    return parcelas.every((p) => !!p.vencimento && p.valor > 0);
  }, [parcelas]);

  // Edita vencimento de uma parcela
  const handleVencimentoChange = useCallback(
    (idx: number, date: Date) => {
      const isoDate = fmt.dates.ISO(date) as DateISO;
      const novasParcelas = parcelas.map((p, i) =>
        i === idx ? { ...p, vencimento: isoDate } : p
      );

      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [parcelas, pagamentos, setPagamentos]
  );

  // Edita valor de uma parcela
  const handleValorChange = useCallback(
    (idx: number, valor: number) => {
      const novasParcelas = parcelas.map((p, i) =>
        i === idx ? { ...p, valor } : p
      );

      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [parcelas, pagamentos, setPagamentos]
  );

  // Adiciona uma nova parcela
  const handleAddParcela = useCallback(() => {
    const maxItemAtual = parcelas.reduce((acc, parcela) => {
      const numero = Number(parcela.item.replace(/\D/g, ""));
      if (!Number.isFinite(numero)) return acc;
      return Math.max(acc, numero);
    }, 0);
    const nextItem = String(maxItemAtual + 1).padStart(3, "0");
    const novasParcelas: ParcelaPayload[] = [
      ...parcelas,
      { item: nextItem, vencimento: "" as DateISO, valor: 0 },
    ];
    const nextManuais = new Set(parcelasManuaisRef.current);
    nextManuais.add(nextItem);
    parcelasManuaisRef.current = nextManuais;
    setPagamentos({ ...pagamentos, parcelas: novasParcelas });
  }, [parcelas, pagamentos, setPagamentos]);

  // Remove uma parcela pelo índice
  const handleRemoveParcela = useCallback(
    (idx: number) => {
      if (parcelas.length <= 1) return;
      if (isParcelaBaseProtheus(idx)) return;
      const parcelasMantidas = parcelas
        .map((parcela, index) => ({ parcela, index }))
        .filter(({ index }) => index !== idx);
      const manuaisAntes = parcelasManuaisRef.current;
      const nextManuais = new Set<string>();
      const novasParcelas = parcelasMantidas.map(({ parcela }, i) => {
        const novoItem = String(i + 1).padStart(3, "0");
        if (manuaisAntes.has(parcela.item)) {
          nextManuais.add(novoItem);
        }
        return { ...parcela, item: novoItem };
      });
      parcelasManuaisRef.current = nextManuais;
      setPagamentos({ ...pagamentos, parcelas: novasParcelas });
    },
    [
      parcelas,
      pagamentos,
      setPagamentos,
      isParcelaBaseProtheus,
    ]
  );


  // ✅ Salva automaticamente as parcelas da API na store quando chegarem
  // MAS só se não houver parcelas válidas editadas pelo usuário
  useEffect(() => {
    if (
      parcelasApi &&
      Array.isArray(parcelasApi) &&
      parcelasApi.length > 0 &&
      canFetch
    ) {
      // Verifica se as parcelas já estão salvas na store para evitar loops
      const currentParcelas =
        usePrenotaStore.getState().draft.pagamentos.parcelas;

      // ✅ NOVO: Verifica se já existem parcelas válidas editadas pelo usuário
      // Se o usuário já editou manualmente (tem parcelas com vencimento e valor), não sobrescreve
      const temParcelasValidasEditadas = currentParcelas.length > 0 && 
        currentParcelas.some((p) => p.vencimento && p.valor > 0);
      const shouldOverwriteByCondicaoChange =
        shouldOverwriteByCondicaoChangeRef.current;

      if (temParcelasValidasEditadas && !shouldOverwriteByCondicaoChange) {
        console.log(
          "💰 [Parcelas] Parcelas já editadas manualmente - preservando edições do usuário"
        );
        return; // Não sobrescreve edições manuais
      }

      // Comparação mais robusta
      const parcelasSaoIguais =
        currentParcelas.length === parcelasApi.length &&
        currentParcelas.every(
          (p, i) =>
            p.item === parcelasApi[i].item &&
            p.vencimento === parcelasApi[i].vencimento &&
            Math.abs(p.valor - parcelasApi[i].valor) < 0.01
        );

      if (!parcelasSaoIguais) {
        console.log(
          "💰 [Parcelas] Salvando automaticamente na store:",
          parcelasApi
        );
        const currentState = usePrenotaStore.getState();
        setPagamentos({
          ...currentState.draft.pagamentos,
          parcelas: parcelasApi,
        });
      }
      if (shouldOverwriteByCondicaoChange) {
        shouldOverwriteByCondicaoChangeRef.current = false;
      }
    }
  }, [parcelasApi, canFetch, setPagamentos]);

  // Função para forçar nova busca e substituir edições
  const handleForceRefetch = useCallback(async () => {
    if (!canFetch) {
      console.warn("💰 [Parcelas] Não pode buscar - campos obrigatórios faltando");
      return;
    }
    
    console.log("💰 [Parcelas] Busca manual iniciada - substituindo edições");
    
    try {
      const result = await refetch();
      
      // ✅ SEMPRE salva quando for busca manual (botão de atualizar)
      if (result.data && Array.isArray(result.data) && result.data.length > 0) {
        console.log("💰 [Parcelas] Salvando parcelas do refetch manual:", result.data);
        const currentState = usePrenotaStore.getState();
        setPagamentos({
          ...currentState.draft.pagamentos,
          parcelas: result.data,
        });
      }
    } catch (error) {
      console.error("💰 [Parcelas] Erro ao buscar parcelas:", error);
    }
  }, [refetch, canFetch, setPagamentos]);

  const restoreParcelasFromProtheusBase = useCallback(
    (patch?: Partial<typeof pagamentos>) => {
      const currentPagamentos = usePrenotaStore.getState().draft.pagamentos;
      const hasVencimentoBase = Object.keys(parcelasBaseProtheus || {}).length > 0;
      const hasValorBase = Object.keys(parcelasValorBaseProtheus || {}).length > 0;
      const hasBase = hasVencimentoBase || hasValorBase;

      if (!hasBase) {
        if (patch) {
          setPagamentos({
            ...currentPagamentos,
            ...patch,
          });
        }
        return;
      }

      const normalizeItemKey = (item?: string) => {
        const trimmed = (item || "").trim();
        if (!trimmed) return "";
        const onlyDigits = trimmed.replace(/\D/g, "");
        if (!onlyDigits) return trimmed;
        const normalized = String(Number(onlyDigits));
        return normalized === "NaN" ? trimmed : normalized;
      };
      const resolveValorBase = (item: string, idx: number) => {
        const exact = parcelasValorBaseProtheus?.[item];
        if (typeof exact === "number" && Number.isFinite(exact)) return exact;

        const normalized = normalizeItemKey(item);
        const fromNormalized = parcelasValorBaseProtheusNormalizado?.[normalized];
        if (
          typeof fromNormalized === "number" &&
          Number.isFinite(fromNormalized)
        ) {
          return fromNormalized;
        }

        const byIndex = parcelasValorBaseProtheusPorIndice?.[idx];
        if (typeof byIndex === "number" && Number.isFinite(byIndex)) return byIndex;

        return undefined;
      };

      const restoredParcelas = currentPagamentos.parcelas.map((parcela, idx) => {
        const baseVencimento = parcelasBaseProtheus?.[parcela.item];
        const baseValor = resolveValorBase(parcela.item, idx);

        if (!baseVencimento && typeof baseValor !== "number") return parcela;

        return {
          ...parcela,
          vencimento: baseVencimento ? (baseVencimento as DateISO) : parcela.vencimento,
          valor: typeof baseValor === "number" ? baseValor : parcela.valor,
        };
      });

      setPagamentos({
        ...currentPagamentos,
        ...patch,
        parcelas: restoredParcelas,
      });
    },
    [
      parcelasBaseProtheus,
      parcelasValorBaseProtheus,
      parcelasValorBaseProtheusNormalizado,
      parcelasValorBaseProtheusPorIndice,
      setPagamentos,
    ]
  );

  return {
    parcelas,
    isLoading,
    isFetching,
    error,
    areParcelasSaved,
    handleVencimentoChange,
    handleValorChange,
    handleAddParcela,
    handleRemoveParcela,
    handleForceRefetch,
    refetch,
    valorNF,
    pagamentos,
    canFetch,
    parcelasBaseProtheus,
    parcelasValorBaseProtheus,
    parcelasValorBaseProtheusNormalizado,
    parcelasValorBaseProtheusPorIndice,
    restoreParcelasFromProtheusBase,
    isParcelaBaseProtheus,
  };
}
